import React, { useState } from 'react';
import { 
  ArrowLeft, Plus, Calendar, User, Activity, Clock, 
  Edit2, Trash2, ChevronDown, ChevronUp, AlertCircle, CheckCircle2, FlaskConical
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { LoadingState } from '../common/LoadingState';
import { AlertCard } from '../common/AlertCard';
import { EmptyState } from '../common/EmptyState';
import { useMyTestSchedules, useCreateTestSchedule, useUpdateTestSchedule, useDeleteTestSchedule } from '../../lib/hooks/useTestSchedules';
import { useTherapistDashboard } from '../../lib/hooks/useDashboards';
import { TestType, TestFrequency, TestSchedule } from '../../lib/api/test-schedules';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface TestPrescriptionManagementProps {
  onBack?: () => void;
}

const TestPrescriptionManagement: React.FC<TestPrescriptionManagementProps> = ({ onBack }) => {
  const [currentTab, setCurrentTab] = useState<'existing' | 'create'>('existing');
  const [expandedSchedule, setExpandedSchedule] = useState<number | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<TestSchedule | null>(null);
  const [deletingSchedule, setDeletingSchedule] = useState<TestSchedule | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Filters
  const [filterActive, setFilterActive] = useState<boolean | undefined>(true);
  const [filterTestType, setFilterTestType] = useState<TestType | undefined>(undefined);

  // Create form state
  const [selectedPatient, setSelectedPatient] = useState<number | null>(null);
  const [testType, setTestType] = useState<TestType>('SPIROMETRY');
  const [frequency, setFrequency] = useState<TestFrequency>('WEEKLY');
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState('');
  const [instructions, setInstructions] = useState('');

  // API hooks
  const { data: schedulesData, isLoading, error, refetch } = useMyTestSchedules({
    is_active: filterActive,
    test_type: filterTestType,
  });

  const { data: dashboardData, isLoading: loadingPatients } = useTherapistDashboard();

  const createMutation = useCreateTestSchedule();
  const updateMutation = useUpdateTestSchedule();
  const deleteMutation = useDeleteTestSchedule();

  const schedules = schedulesData?.schedules || [];
  const patients = dashboardData?.assigned_patients || [];

  // Handle create test schedule
  const handleCreate = async () => {
    if (!selectedPatient) {
      toast.error('Please select a patient');
      return;
    }

    try {
      await createMutation.mutateAsync({
        patient_id: selectedPatient,
        test_type: testType,
        frequency: frequency,
        start_date: startDate,
        end_date: endDate || undefined,
        instructions: instructions || undefined,
      });

      // Reset form
      setSelectedPatient(null);
      setTestType('SPIROMETRY');
      setFrequency('WEEKLY');
      setStartDate(format(new Date(), 'yyyy-MM-dd'));
      setEndDate('');
      setInstructions('');
      setShowCreateDialog(false);
      setCurrentTab('existing');
      toast.success('Test prescription created successfully');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to create test prescription');
    }
  };

  // Handle update
  const handleUpdate = async () => {
    if (!editingSchedule) return;

    try {
      await updateMutation.mutateAsync({
        scheduleId: editingSchedule.id,
        data: {
          is_active: editingSchedule.is_active,
          instructions: editingSchedule.instructions,
        },
      });

      setEditingSchedule(null);
      toast.success('Test prescription updated successfully');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update test prescription');
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deletingSchedule) return;

    try {
      await deleteMutation.mutateAsync(deletingSchedule.id);
      setDeletingSchedule(null);
      toast.success('Test prescription deleted successfully');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete test prescription');
    }
  };

  // Render test type badge
  const getTestTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      SPIROMETRY: 'bg-[#2762AC]/10 text-[#2762AC] border-[#2762AC]',
      SIX_MWT: 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]',
    };

    const labels: Record<string, string> = {
      SPIROMETRY: 'Spirometry',
      SIX_MWT: '6-Minute Walk Test',
    };

    return (
      <Badge className={`${colors[type] || 'bg-gray-100 text-gray-800'} border`} variant="outline">
        {labels[type] || type}
      </Badge>
    );
  };

  // Render frequency badge
  const getFrequencyBadge = (freq: string) => {
    const colors: Record<string, string> = {
      ONCE: 'bg-[#494949]/10 text-[#494949] border-[#494949]',
      DAILY: 'bg-green-100 text-green-700 border-green-200',
      WEEKLY: 'bg-[#2762AC]/10 text-[#2762AC] border-[#2762AC]',
      BIWEEKLY: 'bg-[#F07D13]/10 text-[#F07D13] border-[#F07D13]',
      MONTHLY: 'bg-purple-100 text-purple-700 border-purple-200',
    };

    return (
      <Badge className={`${colors[freq] || 'bg-gray-100 text-gray-800'} border`} variant="outline">
        {freq.replace('_', ' ')}
      </Badge>
    );
  };

  // Loading state
  if (isLoading || loadingPatients) {
    return <LoadingState message="Loading test prescriptions..." />;
  }

  // Error state
  if (error) {
    return (
      <div className="p-4">
        <AlertCard
          type="error"
          title="Failed to Load Test Prescriptions"
          description={(error as any).message || 'An error occurred while loading test prescriptions.'}
          action={<Button onClick={() => refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
              {onBack && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onBack}
                  className="text-[#494949] hover:text-[#2762AC] flex-shrink-0"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              )}
              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                <div className="p-2 bg-[#2762AC]/10 rounded-xl flex-shrink-0">
                  <FlaskConical className="w-5 h-5 sm:w-6 sm:h-6 text-[#2762AC]" />
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="text-[#494949] text-base sm:text-lg truncate">Test Prescription Management</h1>
                  <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Prescribe and manage home tests for your patients</p>
                </div>
              </div>
            </div>
            <Button
              onClick={() => {
                setCurrentTab('create');
                setShowCreateDialog(false);
              }}
              className="bg-[#2762AC] hover:bg-[#1d4a7f] text-white rounded-xl flex-shrink-0 w-full sm:w-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              Prescribe New Tests
            </Button>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <Tabs value={currentTab} onValueChange={(v) => setCurrentTab(v as any)} className="space-y-4 sm:space-y-6">
            <TabsList className="grid w-full max-w-md grid-cols-1 bg-white/80 p-1 rounded-xl">
              <TabsTrigger 
                value="existing" 
                className="rounded-lg data-[state=active]:bg-[#2762AC] data-[state=active]:text-white"
              >
                Existing Prescriptions
              </TabsTrigger>
            </TabsList>

          {/* EXISTING PRESCRIPTIONS TAB */}
          <TabsContent value="existing" className="space-y-3 sm:space-y-4 pb-6">
            {/* Filters */}
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <Label className="text-[#494949] mb-2 block text-sm">Status</Label>
                    <Select
                      value={filterActive === undefined ? 'all' : filterActive ? 'active' : 'inactive'}
                      onValueChange={(v) => setFilterActive(v === 'all' ? undefined : v === 'active')}
                    >
                      <SelectTrigger className="rounded-xl border-gray-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="active">Active Only</SelectItem>
                        <SelectItem value="inactive">Inactive Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-[#494949] mb-2 block text-sm">Test Type</Label>
                    <Select
                      value={filterTestType || 'all'}
                      onValueChange={(v) => setFilterTestType(v === 'all' ? undefined : v as TestType)}
                    >
                      <SelectTrigger className="rounded-xl border-gray-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="SPIROMETRY">Spirometry</SelectItem>
                        <SelectItem value="SIX_MWT">6-Minute Walk Test</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-end">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setFilterActive(true);
                        setFilterTestType(undefined);
                      }}
                      className="w-full rounded-xl border-gray-200 hover:bg-gray-50"
                    >
                      Reset Filters
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Schedules List */}
            {schedules.length === 0 ? (
              <EmptyState
                icon={FlaskConical}
                title="No Test Prescriptions"
                description="You haven't prescribed any home tests yet. Click 'Prescribe New Tests' to get started."
              />
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {schedules.map((schedule) => (
                  <Card key={schedule.id} className="border-0 shadow-sm rounded-2xl overflow-hidden border-l-4 border-l-[#2762AC]">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                        <div className="flex-1 space-y-3 sm:space-y-4 w-full min-w-0">
                          {/* Header */}
                          <div className="flex items-center gap-2 flex-wrap">
                            {getTestTypeBadge(schedule.test_type)}
                            {getFrequencyBadge(schedule.frequency)}
                            {schedule.is_active ? (
                              <Badge className="bg-green-50 text-green-700 border-green-200 border text-xs" variant="outline">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Active
                              </Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-600 border-gray-200 border text-xs" variant="outline">
                                Inactive
                              </Badge>
                            )}
                          </div>

                          {/* Patient Info */}
                          <div className="flex items-center gap-2">
                            <div className="p-2 bg-[#2762AC]/10 rounded-lg flex-shrink-0">
                              <User className="w-4 h-4 text-[#2762AC]" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[#494949] text-sm sm:text-base truncate">{schedule.patient?.name}</p>
                              <p className="text-xs sm:text-sm text-gray-600">UMR: {schedule.patient?.umr_no}</p>
                            </div>
                          </div>

                          {/* Dates */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-[#2762AC] flex-shrink-0" />
                              <span className="truncate">Start: {format(new Date(schedule.start_date), 'MMM dd, yyyy')}</span>
                            </div>
                            {schedule.end_date && (
                              <>
                                <span className="hidden sm:inline">→</span>
                                <span className="truncate sm:ml-0 ml-6">End: {format(new Date(schedule.end_date), 'MMM dd, yyyy')}</span>
                              </>
                            )}
                          </div>

                          {/* Instructions (Expandable) */}
                          {schedule.instructions && (
                            <div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setExpandedSchedule(expandedSchedule === schedule.id ? null : schedule.id)}
                                className="text-[#2762AC] p-0 h-auto hover:bg-transparent text-xs sm:text-sm"
                              >
                                {expandedSchedule === schedule.id ? (
                                  <><ChevronUp className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Hide Instructions</>
                                ) : (
                                  <><ChevronDown className="w-3 h-3 sm:w-4 sm:h-4 mr-1" /> Show Instructions</>
                                )}
                              </Button>
                              {expandedSchedule === schedule.id && (
                                <div className="mt-2 sm:mt-3 p-3 sm:p-4 bg-[#2762AC]/5 rounded-xl text-xs sm:text-sm text-[#494949] border border-[#2762AC]/20">
                                  {schedule.instructions}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Created Date */}
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Clock className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">Prescribed on {format(new Date(schedule.created_at), 'MMM dd, yyyy')}</span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex sm:flex-col gap-2 w-full sm:w-auto sm:ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingSchedule(schedule)}
                            className="flex-1 sm:flex-none h-9 sm:w-9 text-[#2762AC] hover:bg-[#2762AC]/10 border-[#2762AC]/20"
                          >
                            <Edit2 className="w-4 h-4 sm:mr-0 mr-2" />
                            <span className="sm:hidden">Edit</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeletingSchedule(schedule)}
                            className="flex-1 sm:flex-none h-9 sm:w-9 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          >
                            <Trash2 className="w-4 h-4 sm:mr-0 mr-2" />
                            <span className="sm:hidden">Delete</span>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* CREATE NEW TAB */}
          <TabsContent value="create" className="pb-6">
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardHeader className="p-4 sm:p-6 pb-3 sm:pb-4">
                <CardTitle className="text-[#494949] text-base sm:text-lg">Prescribe New Home Test</CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <div className="space-y-4 sm:space-y-5">
                  {/* Patient Selection */}
                  <div>
                    <Label htmlFor="patient" className="text-[#494949] mb-2 block text-sm">Select Patient *</Label>
                    <Select
                      value={selectedPatient?.toString() || ''}
                      onValueChange={(v) => setSelectedPatient(Number(v))}
                    >
                      <SelectTrigger id="patient" className="rounded-xl border-gray-200">
                        <SelectValue placeholder="Choose a patient" />
                      </SelectTrigger>
                      <SelectContent>
                        {patients.length === 0 ? (
                          <div className="p-4 text-center text-gray-500 text-sm">
                            No patients assigned
                          </div>
                        ) : (
                          patients.map((patient: any) => (
                            <SelectItem key={patient.id} value={patient.id.toString()}>
                              {patient.name} - UMR: {patient.umr_no}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Test Type */}
                  <div>
                    <Label htmlFor="testType" className="text-[#494949] mb-2 block text-sm">Test Type *</Label>
                    <Select value={testType} onValueChange={(v) => setTestType(v as TestType)}>
                      <SelectTrigger id="testType" className="rounded-xl border-gray-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SPIROMETRY">
                          <div className="flex items-center gap-2">
                            <Activity className="w-4 h-4 text-[#2762AC]" />
                            <span>Spirometry Test</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="SIX_MWT">
                          <div className="flex items-center gap-2">
                            <Activity className="w-4 h-4 text-[#14CAF4]" />
                            <span>6-Minute Walk Test</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Frequency */}
                  <div>
                    <Label htmlFor="frequency" className="text-[#494949] mb-2 block text-sm">Frequency *</Label>
                    <Select value={frequency} onValueChange={(v) => setFrequency(v as TestFrequency)}>
                      <SelectTrigger id="frequency" className="rounded-xl border-gray-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ONCE">One Time</SelectItem>
                        <SelectItem value="DAILY">Daily</SelectItem>
                        <SelectItem value="WEEKLY">Weekly</SelectItem>
                        <SelectItem value="BIWEEKLY">Bi-Weekly</SelectItem>
                        <SelectItem value="MONTHLY">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date Range - Two columns on larger screens */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {/* Start Date */}
                    <div>
                      <Label htmlFor="startDate" className="text-[#494949] mb-2 block text-sm">Start Date *</Label>
                      <Input
                        id="startDate"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="rounded-xl border-gray-200"
                      />
                    </div>

                    {/* End Date */}
                    <div>
                      <Label htmlFor="endDate" className="text-[#494949] mb-2 block text-sm">End Date (Optional)</Label>
                      <Input
                        id="endDate"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate}
                        className="rounded-xl border-gray-200"
                      />
                    </div>
                  </div>

                  {/* Instructions */}
                  <div>
                    <Label htmlFor="instructions" className="text-[#494949] mb-2 block text-sm">Instructions (Optional)</Label>
                    <Textarea
                      id="instructions"
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                      placeholder="Enter any special instructions for the patient..."
                      rows={4}
                      className="rounded-xl border-gray-200 resize-none"
                    />
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 justify-end pt-4 sm:pt-6 mt-4 sm:mt-6 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedPatient(null);
                      setTestType('SPIROMETRY');
                      setFrequency('WEEKLY');
                      setStartDate(format(new Date(), 'yyyy-MM-dd'));
                      setEndDate('');
                      setInstructions('');
                      setCurrentTab('existing');
                    }}
                    className="rounded-xl border-gray-200 w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreate}
                    disabled={!selectedPatient || createMutation.isPending}
                    className="bg-[#2762AC] hover:bg-[#1d4a7f] text-white rounded-xl w-full sm:w-auto"
                  >
                    {createMutation.isPending ? 'Prescribing...' : 'Prescribe Test'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingSchedule} onOpenChange={() => setEditingSchedule(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-[#494949]">Edit Test Schedule</DialogTitle>
            <DialogDescription>Update the test schedule details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#494949] mb-2 block">Status</Label>
              <Select
                value={editingSchedule?.is_active ? 'active' : 'inactive'}
                onValueChange={(v) => {
                  if (editingSchedule) {
                    setEditingSchedule({ ...editingSchedule, is_active: v === 'active' });
                  }
                }}
              >
                <SelectTrigger className="rounded-xl border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-[#494949] mb-2 block">Instructions</Label>
              <Textarea
                value={editingSchedule?.instructions || ''}
                onChange={(e) => {
                  if (editingSchedule) {
                    setEditingSchedule({ ...editingSchedule, instructions: e.target.value });
                  }
                }}
                rows={4}
                className="rounded-xl border-gray-200 resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setEditingSchedule(null)}
              className="rounded-xl border-gray-200"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdate} 
              disabled={updateMutation.isPending}
              className="bg-[#2762AC] hover:bg-[#1d4a7f] text-white rounded-xl"
            >
              {updateMutation.isPending ? 'Updating...' : 'Update'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingSchedule} onOpenChange={() => setDeletingSchedule(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#494949]">Delete Test Schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this test prescription? This action cannot be undone.
              The patient will no longer see this test in their home tests list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl border-gray-200">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 rounded-xl"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TestPrescriptionManagement;
